import { ISpreadsheetData } from 'components/spreadsheet/types'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import { buildSlicerRuntimeFilters } from 'components/spreadsheet/helpers/functions'

/**
 * Minimum gap between row-state updates while a progressive stream is still arriving. Each update
 * re-renders the grid with a new rows array (a copy of the rows so far); on a wide table every
 * re-render touches hundreds of cells, so the scroll range grows every couple of seconds rather
 * than on every 25k-row batch.
 */
export const PROGRESSIVE_RENDER_INTERVAL_MS = 2000

/** Minimum gap between progress-only updates (the thin bar) while rows are still arriving. */
export const PROGRESS_DISPATCH_INTERVAL_MS = 1000

/**
 * Whether streamed rows can be shown as they arrive, before the stream completes.
 *
 * Rows are rendered progressively only when the batches already come in their final order and
 * nothing client-side would reorder or hide them afterwards — otherwise the grid would show one
 * order (or set) of rows and then shuffle them when the post-stream sort/filter pass runs:
 * - the view is a plain spreadsheet (transpose/matrix/chart… are derived from the full set);
 * - no client-side sorts, filters, slicer selections, search, or validation/comment row filters;
 * - no grouping (groups are computed client-side over the whole row set).
 */
export const canRenderRowsProgressively = (
  spreadsheetData: ISpreadsheetData,
  slicerSelections: Record<string, string[]> | undefined,
  filterSearchRows: boolean
): boolean => {
  const { viewDetails, userConfiguration } = spreadsheetData
  if (viewDetails.type !== ViewTypes.SPREADSHEET) return false
  if (userConfiguration.sortSettings.length > 0) return false
  if (userConfiguration.filterSettings.length > 0) return false
  if (userConfiguration.groupSettings.length > 0) return false
  if (viewDetails.displayValidationErrorRows !== 0 || viewDetails.displayCommentRows !== 0) return false
  if (filterSearchRows) return false
  // Same derivation the post-stream filter pass uses, so a slicer that doesn't touch this view
  // doesn't needlessly hold the rows back.
  if (buildSlicerRuntimeFilters(viewDetails.columns, slicerSelections).length > 0) return false
  return true
}
